import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Card, CardHeader, CardBody, Table, Badge } from "reactstrap";
import axiosInstance from "../../Request";
import { URL } from "../../api";

// Verification Report: onboarding/KYC checkpoints with pass/fail status, from /credit/details.
// Fully defensive — falls back to "Pending" on any missing value and never breaks the Legal tab.
const BLUE = "#1f2d4d";

const StatusBadge = ({ state }) => {
  const map = { pass: ["success", "✓ Pass"], fail: ["danger", "✕ Fail"], pending: ["secondary", "Pending"] };
  const [color, label] = map[state] || map.pending;
  return <Badge color={color} pill style={{ fontSize: "12px", padding: "6px 12px" }}>{label}</Badge>;
};

// 1 / true => pass, 0 / false => fail, otherwise pending.
const flag = (v) => (v === 1 || v === true ? "pass" : v === 0 || v === false ? "fail" : "pending");
const present = (v) => (v !== null && v !== undefined && v !== "" && v !== 0 ? "pass" : "pending");

const PaymentVerificationReport = () => {
  const { cid, lid } = useParams();
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!cid) return;
    let active = true;
    setLoading(true);
    axiosInstance
      .get(`${URL.CREDIT_DETAILS}?customer_id=${cid}&lead_id=${lid}`)
      .then((r) => { if (active) setD(r.data || {}); })
      .catch(() => { if (active) setD({}); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [cid, lid]);

  const checks = d
    ? [
        { name: "PAN Validation", state: flag(d.panValidation), detail: "Identity verified via PAN detailsPlus" },
        { name: "Account Validation (Penny Drop)", state: flag(d.pennyDrop), detail: "Bank account verified via penny drop" },
        {
          name: "Name Match",
          state: d.nameMatchAverage != null ? (Number(d.nameMatchAverage) > 0 ? "pass" : "fail") : flag(d.name_validation),
          detail: d.nameMatchAverage != null ? `Match score: ${d.nameMatchAverage}%` : "Name match validation",
        },
        {
          name: "Bureau Fetch",
          state: present(d.bureauReport),
          detail: d.bureauReport != null && d.bureauReport !== "" ? `Bureau score: ${d.bureauReport}` : "Credit bureau report",
        },
        { name: "Bank Statement", state: flag(d.banking), detail: "Bank statement fetch / analysis" },
      ]
    : [];

  const passed = checks.filter((c) => c.state === "pass").length;

  return (
    <Card className="shadow-sm">
      <CardHeader style={{ background: `linear-gradient(90deg,${BLUE},#33507e)`, border: "none" }}>
        <div className="d-flex justify-content-between align-items-center">
          <h4 className="mb-0" style={{ color: "#fff" }}>Verification Report</h4>
          {!loading && checks.length > 0 && (
            <Badge color="light" pill style={{ color: BLUE }}>{passed}/{checks.length} passed</Badge>
          )}
        </div>
      </CardHeader>
      <CardBody>
        <Table bordered hover responsive className="mb-0">
          <thead style={{ background: "#eef2f8" }}>
            <tr>
              <th style={{ width: "50px", color: BLUE }}>#</th>
              <th style={{ color: BLUE }}>Checkpoint</th>
              <th style={{ width: "140px", color: BLUE }}>Status</th>
              <th style={{ color: BLUE }}>Detail</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="4">Loading…</td></tr>
            ) : checks.length > 0 ? (
              checks.map((c, i) => (
                <tr key={c.name}>
                  <td>{i + 1}</td>
                  <td className="font-weight-bold">{c.name}</td>
                  <td><StatusBadge state={c.state} /></td>
                  <td className="text-muted">{c.detail}</td>
                </tr>
              ))
            ) : (
              <tr><td colSpan="4" className="text-muted">No verification data found</td></tr>
            )}
          </tbody>
        </Table>
      </CardBody>
    </Card>
  );
};

export default PaymentVerificationReport;
